import { Compass } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { PageHeader } from "../components/PageHeader";
import { useTour } from "../components/tour/tourContext";
import { GUIDE_SECTIONS, findGuideSection, type GuideBlock, type GuideSection } from "../lib/guide/sections";
import { TOUR_STEPS, isStepAvailable } from "../lib/tour/steps";
import { useIsMobile } from "../lib/useIsMobile";
import { cardStyle, ctaSmallStyle } from "../components/styles";

interface GuideProps {
  /** Section id from the hash (`#/guide/<id>`), or null for the top. */
  section: string | null;
  onSectionChange: (id: string | null) => void;
}

/**
 * The Guide: every part of the product explained in plain words, from the
 * pure `GUIDE_SECTIONS` array (spec §2). The tour launcher sits on top so
 * the old sidebar behaviour ("Guide" = start the tour) is one click away.
 */
export function Guide({ section, onSectionChange }: GuideProps): React.JSX.Element {
  const isMobile = useIsMobile();
  const { start, goToStep, hasVideo } = useTour();
  const current = findGuideSection(section);
  const refs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (!current) return;
    const el = refs.current[current.id];
    el?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [current]);

  const showMe = (s: GuideSection): void => {
    if (!s.tourStepId) return;
    const idx = TOUR_STEPS.findIndex((t) => t.id === s.tourStepId);
    if (idx >= 0) goToStep(idx); else start();
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <PageHeader eyebrow="Guide" title="How this works." subtitle="Every screen, every button, in plain words." />

      <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <Compass size={22} strokeWidth={1.75} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Show me around</div>
          <div style={{ fontSize: 13, color: "var(--ql-fg-muted)" }}>A two-minute walk-through that builds one video with you.</div>
        </div>
        <button type="button" style={ctaSmallStyle} onClick={() => start()}>Start the tour</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "220px 1fr", gap: 28 }}>
        {isMobile ? (
          <select
            aria-label="Guide sections"
            value={current?.id ?? ""}
            onChange={(e) => onSectionChange(e.target.value || null)}
            style={{ padding: "10px 12px", borderRadius: 10, fontFamily: "inherit" }}
          >
            <option value="">Top</option>
            {GUIDE_SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        ) : (
          <nav aria-label="Guide sections" style={{ position: "sticky", top: 32, alignSelf: "flex-start", display: "flex", flexDirection: "column", gap: 3 }}>
            {GUIDE_SECTIONS.map((s) => {
              const isActive = current?.id === s.id;
              return (
                <a
                  key={s.id}
                  href={`#/guide/${s.id}`}
                  aria-current={isActive ? "page" : undefined}
                  onClick={(e) => { e.preventDefault(); onSectionChange(s.id); }}
                  style={{
                    padding: "8px 12px", borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: "none",
                    color: isActive ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                    background: isActive ? "rgba(255,213,0,.10)" : "transparent",
                  }}
                >
                  {s.title}
                </a>
              );
            })}
          </nav>
        )}

        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 28 }}>
          {GUIDE_SECTIONS.map((s) => (
            <section
              key={s.id}
              id={`guide-${s.id}`}
              ref={(el) => { refs.current[s.id] = el; }}
              style={{ ...cardStyle, outline: current?.id === s.id ? "1px solid rgba(255,213,0,.35)" : undefined }}
            >
              <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>{s.title}</h2>
              <p style={{ margin: "0 0 14px", color: "var(--ql-fg-muted)", fontSize: 14 }}>{s.lede}</p>
              {s.blocks.map((b, i) => <Block key={i} block={b} />)}
              {s.tourStepId && (() => {
                // A step gated on `requiresVideo` has nowhere useful to land
                // without one — matching `advance`/`back`, which never stop
                // on such a step either, "Show me" must not offer it.
                const step = TOUR_STEPS.find((t) => t.id === s.tourStepId);
                return step && isStepAvailable(step, hasVideo) ? (
                  <button type="button" style={{ ...ctaSmallStyle, marginTop: 12 }} onClick={() => showMe(s)}>
                    Show me
                  </button>
                ) : (
                  <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--ql-fg-subtle)", fontStyle: "italic" }}>
                    Available once you have a video.
                  </div>
                );
              })()}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function Block({ block }: { block: GuideBlock }): React.JSX.Element {
  if (block.kind === "p") return <p style={{ margin: "0 0 12px", lineHeight: 1.6, fontSize: 14 }}>{block.text}</p>;
  if (block.kind === "list") {
    return (
      <ul style={{ margin: "0 0 12px", paddingLeft: 20, lineHeight: 1.6, fontSize: 14 }}>
        {block.items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    );
  }
  return (
    <div style={{ margin: "0 0 12px", fontSize: 14, lineHeight: 1.6 }}>
      <span style={{ fontWeight: 600, color: "var(--ql-yellow)" }}>{block.label}. </span>
      {block.text}
    </div>
  );
}
